module.exports = { createUpgradeGate, compareVersions, getUpgradeAction, fetchWebVersion };

function compareVersions(a, b) {

    const parse = (value) => {

        if (typeof value !== 'string' || !/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/.test(value)) {
            return null;
        }

        const parts = value.split('.').map(Number);
        return parts.every(Number.isSafeInteger) ? parts : null;
    };
    const left = parse(a);
    const right = parse(b);

    if (!left || !right) {
        return null;
    }

    for (let i = 0; i < 3; ++i) {
        if (left[i] !== right[i]) {
            return left[i] > right[i] ? 1 : -1;
        }
    }

    return 0;
}

function createUpgradeGate({ installedVersion, isNative, readWebVersion, storage, onChange, now = Date.now }) {

    let minimum;
    let revision = 0;
    let observation;
    const dismissed = new Set();
    const key = () => `nearpeer:app-upgrade:${installedVersion}:${minimum}`;
    const canPersistDismissal = () => {

        const probeKey = `${key()}:storage-check`;

        try {
            storage.setItem(probeKey, 'available');
            const available = storage.getItem(probeKey) === 'available';
            storage.removeItem(probeKey);
            return available;
        }
        catch (err) {
            return false;
        }
    };
    const isDismissed = () => {

        if (dismissed.has(key())) {
            return true;
        }

        try {
            return Boolean(storage?.getItem(key()));
        }
        catch (err) {
            return false;
        }
    };

    return {
        async update(value) {

            const current = ++revision;
            const nextMinimum = compareVersions(value, value) === 0 ? value : undefined;

            if (nextMinimum !== minimum) {
                observation = undefined;
                onChange(false, nextMinimum, installedVersion);
            }

            minimum = nextMinimum;

            if (compareVersions(value, installedVersion) !== 1 || isDismissed() || (!isNative && !canPersistDismissal())) {
                onChange(false, minimum, installedVersion);
                return;
            }

            let available = isNative;

            if (!isNative) {
                if (!observation || (!observation.pending && now() >= observation.expiresAt)) {
                    const fresh = {};
                    observation = fresh;
                    const check = async () => {

                        try {
                            const metadata = await readWebVersion();
                            const comparison = compareVersions(metadata?.appVersion, value);
                            return comparison === 0 || comparison === 1;
                        }
                        catch (err) {
                            return false;
                        }
                    };
                    fresh.pending = check().then((result) => {

                        fresh.available = result;
                        fresh.expiresAt = now() + 30000;
                        fresh.pending = undefined;
                        return result;
                    });
                }

                available = observation.pending ? await observation.pending : observation.available;
            }

            if (current === revision) {
                // A refresh must be able to remember its attempt even if stale HTML loads again.
                onChange(Boolean(available) && !isDismissed() && (isNative || canPersistDismissal()), minimum, installedVersion);
            }
        },
        dismiss(reason) {

            ++revision;
            dismissed.add(key());
            let persisted = false;

            try {
                storage.setItem(key(), reason);
                persisted = storage.getItem(key()) === reason;
            }
            catch (err) {
                // Continuing still works; browser callers must skip reload without persistence.
            }

            onChange(false, minimum, installedVersion);
            return persisted;
        }
    };
}

function getUpgradeAction({ isNative, platform, isStaging, isTeams, testflightUrl }) {

    if (!isNative) {
        return { label: 'Refresh' };
    }

    if (platform === 'ios') {
        if (isStaging) {
            const publicUrl = typeof testflightUrl === 'string' && /^https:\/\/testflight\.apple\.com\/(?:join\/[A-Za-z0-9]+\/?|)$/.test(testflightUrl);
            return { label: 'TestFlight', href: publicUrl ? testflightUrl : 'https://apps.apple.com/app/testflight/id899247664' };
        }

        return {
            label: 'App Store',
            href: isTeams ? 'https://itunes.apple.com/us/app/nearpeer-for-teams/id6445984458' : 'https://itunes.apple.com/us/app/nearpeer-for-college-students/id1300243604'
        };
    }

    if (platform === 'android') {
        if (isStaging) {
            return { kind: 'manual', label: 'Continue for now' };
        }

        return {
            label: 'Play Store',
            href: `https://play.google.com/store/apps/details?id=${isTeams ? 'net.nearpeer.app_business' : 'net.nearpeer.app'}`
        };
    }

    return { label: 'Refresh' };
}

async function fetchWebVersion(fetch) {

    const controller = typeof AbortController === 'undefined' ? undefined : new AbortController();
    let timer;
    const timeout = new Promise((resolve, reject) => {

        timer = setTimeout(() => {

            controller?.abort();
            reject(new Error('Version unavailable'));
        }, 5000);
    });
    const read = async () => {

        const response = await fetch('/frontend/_version', { cache: 'no-store', signal: controller?.signal });

        if (!response.ok) {
            throw new Error('Version unavailable');
        }

        return response.json();
    };

    try {
        return await Promise.race([read(), timeout]);
    }
    finally {
        clearTimeout(timer);
    }
}
